/* eslint-disable @typescript-eslint/naming-convention */
// deps
import React, { useContext, useCallback } from "react";

// internal-deps
import {
    SavingsLineItemData,
    SavingsRightSectionType,
    ReviewedCartApiResponse,
    ApplyCouponsWidgetInfo,
    GiftingApplyCouponWidgetInfo,
    MROChildCart,
} from "@swiggy-private/cart-types";
import { MROReviewedCart } from "@cart-client/interfaces/MROFoodCart";
import {
    getAppliedRestaurantCouponCode,
    getAppliedPaymentCouponCode,
    TooltipContainer,
    getAppliedGiftingCouponCodes,
    getRestaurantCouponCode,
} from "@swiggy-private/cart-ui";
import { format, isEmpty } from "@swiggy-private/js-utils";
import {
    getMROChildCartFromApplyCouponWidgetInfo,
    getMROChildCartRestaurantName,
    isMROFlow,
} from "@cart-client/helpers/MROFoodCart";

// contexts
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";
import { CartContext } from "@cart-client/contexts/CartContext";

// hooks
import { useCouponSavingsActions } from "./useCouponSavingsActions";
import { useGiftingCouponCodeAnalytics } from "./useGiftingCouponCodeAnalytics";

// helpers
import {
    getAppliedCouponSubtitleText,
    getAppliedCouponTitleText,
    getCouponExpiryInDays,
    getAppliedCouponIcon,
    showCouponCodesListInGiftingCartSavingsCorner,
} from "@cart-client/helpers/Coupon";

// constants
import { APPLY_COUPON_TEXTS, CouponType } from "@cart-client/constants/Coupon";
import { CART_V2_APPLY_COUPON_ICON } from "@cart-client/constants/index";

type ApplyCouponWidgetData = ReviewedCartApiResponse["apply_coupon_widget_info"];

export interface UseAppliedCouponSavingsReturnType {
    getAppliedCouponSavingsItems: (
        icon: string,
        applyCouponWidgetData: ApplyCouponWidgetData,
        couponType?: CouponType,
    ) => SavingsLineItemData[];
}

export const useAppliedCouponSavingsData = (
    applyPaymentCouponWidgetData: ApplyCouponsWidgetInfo["payment_coupon"],
    onSavingsLineItemLoad: (
        title?: string,
        subtitle?: string,
        isPaymentCouponWidget?: boolean,
        restaurantId?: string | number,
    ) => void,
    isNewPORFlow: boolean,
): UseAppliedCouponSavingsReturnType => {
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse } = cartState;
    const { data } = useContext(CartFlowContext);
    const isMROCart = isMROFlow(data?.cartSource);

    // this coupon code works for food cart BAU flow
    const couponCode = getAppliedRestaurantCouponCode(cartResponse?.data);
    const paymentCouponCode = getAppliedPaymentCouponCode(cartResponse?.data);
    const giftingCouponCodes = getAppliedGiftingCouponCodes(cartResponse?.data);

    const { handleImpressionForGiftingCouponCode } = useGiftingCouponCodeAnalytics();

    const { onApplyCouponSectionClick, onExtraSavingsTooltipClick } = useCouponSavingsActions({
        applyPaymentCouponWidgetData,
    });

    // Extra-savings data is derived from the scoped widget data passed in per rendering detail.
    const getExtraSavingsData = useCallback(
        (applyCouponWidgetData: ApplyCouponWidgetData) => {
            if (isEmpty(applyCouponWidgetData) || isEmpty(applyCouponWidgetData.extra_savings)) {
                return null;
            }
            const expiryInDays = getCouponExpiryInDays(applyCouponWidgetData.extra_saving_expiry_in_ms ?? "0");
            let title = format(APPLY_COUPON_TEXTS.X_OFF_ON_NEXT_ORDER, applyCouponWidgetData.extra_savings);

            if (isMROCart) {
                const childCart = getMROChildCartFromApplyCouponWidgetInfo(
                    cartResponse as MROReviewedCart,
                    applyCouponWidgetData,
                );
                const rxName = getMROChildCartRestaurantName(childCart);
                if (!isEmpty(childCart) && !isEmpty(rxName)) {
                    title = format(
                        APPLY_COUPON_TEXTS.X_OFF_ON_NEXT_ORDER_ON_RX,
                        applyCouponWidgetData.extra_savings,
                        rxName,
                    );
                }
            }

            return {
                title: title,
                expiryMessage: format(APPLY_COUPON_TEXTS.EXPIRES_IN_DAYS, String(expiryInDays)),
                tooltipBody: format(APPLY_COUPON_TEXTS.EXTRA_SAVINGS_TOOLTIP_CONTENT, String(expiryInDays)),
            };
        },
        [cartResponse, isMROCart],
    );

    const handleExtraSavingsTooltipClick = useCallback(
        (applyCouponWidgetData: ApplyCouponWidgetData) => () => onExtraSavingsTooltipClick(applyCouponWidgetData),
        [onExtraSavingsTooltipClick],
    );

    const getExtraSavingsLineItem = useCallback(
        (icon: string, applyCouponWidgetData: ApplyCouponWidgetData) => {
            const extraSavingsData = getExtraSavingsData(applyCouponWidgetData);
            if (isEmpty(extraSavingsData)) {
                return null;
            }

            return {
                icon: icon || CART_V2_APPLY_COUPON_ICON,
                title: {
                    titleNode: (
                        <TooltipContainer
                            displayText={extraSavingsData.title}
                            toolTipMeta={{
                                title: extraSavingsData.title,
                                description: extraSavingsData.tooltipBody,
                            }}
                            onClickTooltipEvent={handleExtraSavingsTooltipClick(applyCouponWidgetData)}
                        />
                    ),
                },
                subtitle: {
                    value: extraSavingsData.expiryMessage,
                },
                rightSection: {
                    type: SavingsRightSectionType.LABEL,
                    value: APPLY_COUPON_TEXTS.APPLIED,
                },
                onLoad: () => {
                    onSavingsLineItemLoad(
                        extraSavingsData.title,
                        extraSavingsData.expiryMessage,
                        false,
                        applyCouponWidgetData?.mroChildCartRestaurantId,
                    );
                },
            };
        },
        [getExtraSavingsData, handleExtraSavingsTooltipClick, onSavingsLineItemLoad],
    );

    const getAppliedCouponSavingsItems = useCallback(
        (
            icon: string,
            applyCouponWidgetData: ApplyCouponWidgetData,
            couponType: CouponType = CouponType.RESTAURANT,
        ): SavingsLineItemData[] => {
            // BAU/gifting: use the global applied code (unchanged behavior).
            // MRO: global applied code is empty, so fall back to the per-rendering-detail
            // (per child cart) widget data coupon code.
            let appliedCouponCode = couponCode || applyCouponWidgetData?.coupon_code;
            if (isMROCart) {
                const childCart = getMROChildCartFromApplyCouponWidgetInfo(
                    cartResponse as MROReviewedCart,
                    applyCouponWidgetData,
                );
                if (!isEmpty(childCart)) {
                    appliedCouponCode = getRestaurantCouponCode(childCart as MROChildCart, applyCouponWidgetData);
                }
            }

            if (couponType === CouponType.RESTAURANT && isEmpty(appliedCouponCode) && isEmpty(giftingCouponCodes)) {
                return [];
            }

            if (couponType === CouponType.PAYMENT && isEmpty(paymentCouponCode)) {
                return [];
            }

            const titleText = getAppliedCouponTitleText({
                couponType,
                applyCouponWidgetData,
                applyPaymentCouponWidgetData,
                couponCode: appliedCouponCode,
                paymentCouponCode,
                giftingCouponCodes,
                cartResponse,
                isMROCart,
            });
            const subtitleText = getAppliedCouponSubtitleText(couponType, isNewPORFlow);

            let couponCodeList;
            if (showCouponCodesListInGiftingCartSavingsCorner(applyCouponWidgetData)) {
                couponCodeList = {
                    applied: true,
                    codes: (applyCouponWidgetData as GiftingApplyCouponWidgetInfo)?.coupon_codes,
                    impressionEventCallback: handleImpressionForGiftingCouponCode,
                };
            }

            const appliedSavingsLineItem: SavingsLineItemData = {
                icon: icon || getAppliedCouponIcon(couponType),
                title: {
                    value: titleText,
                },
                subtitle: {
                    value: subtitleText,
                    icon: true,
                    onClick: () =>
                        onApplyCouponSectionClick({
                            isPaymentCouponWidget: couponType === CouponType.PAYMENT,
                            applyCouponWidgetData,
                        }),
                },
                rightSection: {
                    type: SavingsRightSectionType.LABEL,
                    value: APPLY_COUPON_TEXTS.APPLIED,
                },
                onLoad: () => {
                    onSavingsLineItemLoad(
                        titleText,
                        subtitleText,
                        couponType === CouponType.PAYMENT,
                        applyCouponWidgetData?.mroChildCartRestaurantId,
                    );
                },
                couponCodeList,
            };

            const appliedExtraSavingsLineItem = getExtraSavingsLineItem(icon, applyCouponWidgetData);

            return appliedExtraSavingsLineItem && couponType === CouponType.RESTAURANT
                ? [appliedSavingsLineItem, appliedExtraSavingsLineItem]
                : [appliedSavingsLineItem];
        },
        [
            applyPaymentCouponWidgetData,
            couponCode,
            getExtraSavingsLineItem,
            giftingCouponCodes,
            isNewPORFlow,
            onApplyCouponSectionClick,
            onSavingsLineItemLoad,
            paymentCouponCode,
            handleImpressionForGiftingCouponCode,
            cartResponse,
            isMROCart,
        ],
    );

    return { getAppliedCouponSavingsItems };
};
